import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Link } from "react-router-dom";
import Text from "@/components/ui/text";
import { useTranslation } from "react-i18next";
import SingleSelect, {
  singleSelectVariant
} from "@/components/controls/SingleSelect";
import SingleInput from "@/components/controls/SingleInput";
import { fromPercentInput, toPercentInput } from "@/utils/numbers";
import DateSelection from "@/components/controls/DateSelection";
import {
  createGetFieldKey,
  createGetStableFieldKey,
  stripHtml
} from "@/utils/strings";
import { cn } from "@/utils/utils";
import { decodeCheckbox } from "@/utils/fieldRendering/legacyBoolTokens";
import { Progress } from "@/components/ui/progress";
import { startOfDay } from "date-fns";
import {
  simpleDate,
  toCalendarDate,
  toDateOnlyISO,
  toLocalDate
} from "@/utils/datetime";
import ScoreBars, {
  ScoreStrength,
  ScoreStrengthFromValue
} from "@/components/controls/ScoreBars";
import { ValidationRule } from "@/utils/validation";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import InlineLoader from "@/components/ui/inline-loader";
import { SettingsThresholds } from "@/entities/settings/types";
import MultiInput from "@/components/controls/MultiInput";
import TextWithEditLink from "@/components/controls/TextWithEditLink";
import TextWithHoverAction from "@/components/controls/TextWithHoverAction";
import { DrawerStateItem } from "@/services/hooks/entities/useQuickViewDrawer";
import { EntityData } from "@/services/api/types";
import IconContainer from "@/components/ui/icon-container";
import { IconsMap } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import SingleCheckbox from "@/components/ui/SingleCheckbox";
import Checkmark from "@/components/ui/checkmark";
import { MultiSelect } from "@/components/controls/MultiSelect";
import Tabs from "@/components/controls/Tabs";
import TextEditor from "@/components/controls/TextEditor/TextEditor";
import DateRangeField from "@/components/controls/DateRangeField";
import EntitySelectField from "@/components/controls/EntitySelectField";
import EntityMultiSelectField from "@/components/controls/EntityMultiSelectField";
import CreatePartnerModal from "@/pages/partners/components/CreatePartnerModal";
import CreatePartnerContactModal from "@/pages/partners/contacts/components/CreatePartnerContactModal";
import { resolveCreatePartnerContactPartnerId } from "@/components/layouts/detail-fields-helpers";
import CurrencySelectField from "@/components/controls/CurrencySelectField";
import { PhoneControl } from "@/utils/fieldRendering/controls/PhoneControl";
import { getPhoneValueToSave } from "@/components/controls/Form/helpers";
import { InfoCircle } from "@dreamhub/designsystem/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import {
  forEach,
  get,
  isEmpty,
  isNull,
  isNumber,
  isUndefined,
  keys,
  map,
  mapValues
} from "lodash";
import { camelizeFieldKey } from "@/utils/fieldKeys";
import { getCreateNewLabel } from "@/utils/entityFieldHelpers";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import { AddButtonFooter } from "@/components/ui/add-button-footer";
import { Button } from "@/components/ui/button";
import {
  createAddNewOptionHandler,
  CreateNewOptionContext,
  shouldShowCreateNewButton,
  shouldDisableCreateNewButton,
  getParsedFieldValue,
  formatMultiSelectValueForDisplay
} from "@/utils/entityFieldsUtils/shared";
import ClickableUrlField from "@/components/controls/ClickableUrlField";
import { DetailAttachmentsField } from "@/components/controls/DetailAttachmentsField";
import ReadOnlyAttachmentsField from "@/components/controls/ReadOnlyAttachmentsField";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import {
  parseFileFieldValue,
  serializeFileFieldValue
} from "@/utils/entityFieldsUtils/fileFieldUtils";
import ButtonField, {
  ButtonFieldConfig
} from "@/components/controls/ButtonField";
import { DetailFieldRow } from "@/components/layouts/DetailFieldRow";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";

export interface BaseEntityData {
  id: string;
}

type FieldType =
  | "select"
  | "editableInput"
  | "editableMultiInput"
  | "date"
  | "readOnlyDate"
  | "scoreBar"
  | "strengthBar"
  | "readOnlyText"
  | "link"
  | "linkedField"
  | "textWithHoverAction"
  | "switch"
  | "checkbox"
  | "checklistItem"
  | "textarea"
  | "multiSelect"
  | "dateRange"
  | "clickableUrl"
  | "button"
  | "currencySelect"
  | "phone"
  | "attachments"
  | "readOnlyAttachments";

// Detail-field formatters receive the raw entity value, whose type varies per
// field, and return the row's plain text.
export type FieldValueFormatter = (value: unknown) => string;

interface BaseFieldConfig {
  type: FieldType;
  label?: string;
  icon?: string;
  additionalProps?: Record<string, any>;
  selectedValue?: string | number | null;
  suffixComponent?: JSX.Element;
  followingComponent?: JSX.Element;
  infoTooltip?: string;
  helpText?: string;
  layout?: "full" | "half";
  pairedWith?: string;
  keepOrder?: boolean;
  required?: boolean;
  disabled?: boolean;
  disabledTooltipText?: string;
}

interface SelectFieldConfig extends BaseFieldConfig {
  type: "select";
  options: any[];
  formatter?: (value: any) => string;
  renderOptionIcon?: (value: number | string) => JSX.Element;
  defaultValue?: any;
  entity?: EntityType;
  showSearch?: boolean;
  fixedOptions?: any[];
  popoverClassName?: string;
  treatNullAsValue?: boolean;
  disabled?: boolean;
  disabledTooltipText?: string;
  searchTerm?: string;
  onSearch?: (newSearchVal: string) => void;
  isLoading?: boolean;
  dependencies?: string[];
  onChange?: (value: string | number) => void;
  isEntityField?: boolean;
  displayType?: string;
  config?: any;
  onCreateNew?: () => void;
  createNewLabel?: string;
  customOnChange?: (value: string | number) => void;
  allowUsersToAdd?: boolean;
  fieldId?: string;
  variant?: singleSelectVariant;
  dependentValues?: Record<string, any>;
  showFixedOptions?: boolean;
  excludeIds?: string[];
}

interface SingleInputFieldConfig extends BaseFieldConfig {
  type: "editableInput";
  initialValue?: string | number;
  validation?: {
    customRules: ValidationRule[];
  };
  isMonetary?: boolean;
  isPercentage?: boolean;
  isPretty?: boolean;
  isNumericOnly?: boolean;
  maxDigits?: number;
  currencySymbol?: string;
}

interface MultiInputFieldConfig extends BaseFieldConfig {
  type: "editableMultiInput";
  initialValue?: string | number[];
  validation?: {
    customRules: ValidationRule[];
  };
  isMonetary?: boolean;
  isPretty?: boolean;
  currencySymbol?: string;
}

interface DateFieldConfig extends BaseFieldConfig {
  type: "date" | "readOnlyDate";
  // Read-only dates only: what a blank value renders as.
  emptyText?: string;
  // A timestamp shown on the viewer's local day rather than its stored day.
  localDay?: boolean;
  onChange?: (value: string) => void;
  disabled?: boolean;
  disabledTooltipText?: string;
  availableFrom?: Date;
  availableTo?: Date;
  // Offers clear even when required — for a field whose flag is enforced only on create.
  clearable?: boolean;
}

interface ScoreBarFieldConfig extends BaseFieldConfig {
  type: "scoreBar";
  suffix?: string;
  levels?: SettingsThresholds;
}

interface StrengthBarFieldConfig extends BaseFieldConfig {
  type: "strengthBar";
  getLabel: (value: ScoreStrength) => string;
  scoreStrengthThresholds?: SettingsThresholds;
}

interface ReadOnlyTextFieldConfig extends BaseFieldConfig {
  type: "readOnlyText";
  formatter?: (value: any) => React.ReactNode;
  fieldKey?: string; // For linked fields - the original field key to check for _resolved version
}

interface LinkFieldConfig extends BaseFieldConfig {
  type: "link";
  to?: string;
  formatter?: (value: any) => string;
}

interface LinkedFieldConfig extends BaseFieldConfig {
  type: "linkedField";
  linkedEntityType: EntityType;
  linkedEntity: DrawerStateItem;
  onUpdate?: (updatedEntity: EntityData) => void;
  formatter?: (value: any) => string;
  displayType?: string;
}

interface TextWithHoverActionFieldConfig extends BaseFieldConfig {
  type: "textWithHoverAction";
  actionLabel: string;
  actionAriaLabel?: string;
  actionDataTest?: string;
  // Withhold both to render the value with no action at all.
  to?: string;
  replace?: boolean;
  onAction?: () => void;
  formatter?: FieldValueFormatter;
  // As on readOnlyText: the original field key, so a value the API emits on a
  // `_resolved` companion field still resolves.
  fieldKey?: string;
}

interface SwitchFieldConfig extends BaseFieldConfig {
  type: "switch";
  options: { id: string; displayName: string }[];
  defaultValue?: string;
  onChange?: (value: string) => void;
}

interface CheckboxFieldConfig extends BaseFieldConfig {
  type: "checkbox";
  defaultValue?: boolean;
  onChange?: (value: boolean) => void;
}

interface ChecklistItemFieldConfig extends BaseFieldConfig {
  type: "checklistItem";
  defaultValue?: boolean;
  onChange?: (value: boolean) => void;
}

interface TextareaFieldConfig extends BaseFieldConfig {
  type: "textarea";
  initialValue?: string;
  validation?: {
    customRules: ValidationRule[];
  };
  disabled?: boolean;
}

interface MultiSelectFieldConfig extends BaseFieldConfig {
  type: "multiSelect";
  options: any[];
  defaultValue?: any[];
  onChange?: (value: any[]) => void;
  onCreateNew?: () => void;
  createNewLabel?: string;
  customOnChange?: (value: any[]) => void;
  isEntityField?: boolean;
  displayType?: string;
  config?: any;
  showSearch?: boolean;
  onSearch?: (newSearchVal: string) => void;
  isLoading?: boolean;
  searchPlaceholder?: string;
  allowUsersToAdd?: boolean;
  fieldId?: string;
  dependentValues?: Record<string, string | null>;
  disabled?: boolean;
}

interface DateRangeFieldConfig extends BaseFieldConfig {
  type: "dateRange";
  onChange?: (value: { fromDate: string; toDate: string }) => void;
  disabled?: boolean;
  disabledTooltipText?: string;
}

interface ClickableUrlFieldConfig extends BaseFieldConfig {
  type: "clickableUrl";
  initialValue?: string;
  displayText?: string;
  readOnly?: boolean;
  validation?: {
    customRules: ValidationRule[];
  };
  onSave?: (value: string) => void;
}

interface ButtonFieldConfigType extends BaseFieldConfig {
  type: "button";
  config: ButtonFieldConfig;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

interface CurrencySelectFieldConfig extends BaseFieldConfig {
  type: "currencySelect";
  placeholder?: string;
  disabled?: boolean;
}

interface PhoneFieldConfig extends BaseFieldConfig {
  type: "phone";
}

interface AttachmentsDetailFieldConfig extends BaseFieldConfig {
  type: "attachments";
  accept?: string;
  isSingleFile?: boolean;
}

interface ReadOnlyAttachmentsDetailFieldConfig extends BaseFieldConfig {
  type: "readOnlyAttachments";
  fieldKey?: string;
  // Read-only file values reach the field through several routes (a linked
  // entity's resolved value, an SCO answer, the raw stored value), so the
  // config that builds them resolves the attachments itself.
  attachmentsResolver?: (value: unknown) => SavedAttachmentInfo[];
  emptyText?: string;
  // Set only for a file value owned by a linked entity, so the row offers the
  // same "Edit in {Entity}" action every other linked field does. Both are
  // withheld for the read-only routes that have no source entity to edit in
  // (an SCO answer, a formula field, an unresolvable link target).
  linkedEntityType?: EntityType;
  linkedEntity?: DrawerStateItem;
}

export type FieldConfig =
  | SelectFieldConfig
  | SingleInputFieldConfig
  | MultiInputFieldConfig
  | DateFieldConfig
  | ScoreBarFieldConfig
  | StrengthBarFieldConfig
  | ReadOnlyTextFieldConfig
  | LinkFieldConfig
  | LinkedFieldConfig
  | TextWithHoverActionFieldConfig
  | SwitchFieldConfig
  | CheckboxFieldConfig
  | ChecklistItemFieldConfig
  | TextareaFieldConfig
  | MultiSelectFieldConfig
  | DateRangeFieldConfig
  | ClickableUrlFieldConfig
  | ButtonFieldConfigType
  | CurrencySelectFieldConfig
  | PhoneFieldConfig
  | AttachmentsDetailFieldConfig
  | ReadOnlyAttachmentsDetailFieldConfig;

export interface FieldsConfig {
  [field: string]: FieldConfig;
}

export interface DetailFieldsSection {
  id: string | number;
  name: string;
  fieldsConfig: FieldsConfig;
}

export interface DetailFieldsContext extends CreateNewOptionContext {
  setView?: (view: "deals" | "companies" | "people") => void;
  onCompanySelect?: (companyId: string) => void;
  onProductSelect?: (productId: string) => void;
  // Modal functionality for creating new options
  showCreateOptionModal?: boolean;
  newOptionName?: string;
  currentFieldForNewOption?: { fieldId: string; fieldKey: string } | null;
  handleCreateNewOption?: (optionName: string) => Promise<void>;
  onUpdateField?: (
    field: string,
    newValue: string | number | boolean | string[] | any,
    additionalFields?: { [fieldName: string]: string | number }
  ) => void;
  // Currencies support
  currencies?: string[];
  accountCurrency?: string;
}

export interface FieldItem {
  component: JSX.Element | null;
  icon: string;
}

interface Props<T extends BaseEntityData> {
  entity?: T;
  fieldsConfig: FieldsConfig;
  onUpdateField: (
    field: string,
    newValue: string | number | boolean | string[] | any,
    additionalFields?: { [fieldName: string]: string | number }
  ) => void;
  className?: string;
  containerClassName?: string;
  tPrefix: string;
  isDrawerOpen?: boolean;
  isQuickView?: boolean;
  context?: DetailFieldsContext;
  hideContainer?: boolean;
  // Rendered after the unsectioned fields, one heading per section, in the
  // detailed view only (the quick view and hideContainer callers ignore it)
  sections?: DetailFieldsSection[];
}

export const isEmptyFieldValue = (
  value: string | number | any[],
  type: FieldType,
  treatNullAsValue?: boolean
) => {
  if (isNumber(value) && !isNaN(value)) {
    return false;
  }

  if (type === "select") {
    return treatNullAsValue
      ? isUndefined(value)
      : isNumber(value)
        ? isNull(value)
        : isEmpty(value);
  }

  if (type === "multiSelect") {
    if (Array.isArray(value)) {
      const filteredValue = value.filter(
        (v) => v !== "none" && v !== "null" && v !== null && v !== undefined
      );
      return filteredValue.length === 0;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      return (
        trimmed === "" ||
        trimmed === "[]" ||
        trimmed === "['']" ||
        trimmed === '[""]' ||
        trimmed === '["none"]' ||
        trimmed === "['none']"
      );
    }
    return isEmpty(value);
  }

  return isEmpty(value);
};

// The calendar day a date field shows: the local day for a timestamp field, the stored day otherwise.
// toCalendarDate counts a value at UTC midnight (to the second) as date-only, so it keeps its UTC day.
const toFieldDay = (value: string, localDay?: boolean): Date =>
  localDay ? startOfDay(toCalendarDate(value)) : toLocalDate(value);

const getParsedFieldValueForDetailFields = (
  value: any,
  config: FieldConfig
) => {
  if (config.type === "select") {
    const formattedValue =
      !isNaN(Number(value)) && !isNull(value) ? Number(value) : value;
    return config?.treatNullAsValue
      ? value
      : (formattedValue ?? config.defaultValue);
  }

  if (config.type === "multiSelect") {
    // Handle stringified arrays from API
    if (typeof value === "string" && value.trim() !== "") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          // Filter out "none" values that represent null/empty state
          return parsed.filter(
            (v) => v !== "none" && v !== "null" && v !== null && v !== undefined
          );
        }
      } catch {
        try {
          const arrayStyleMatch = value.match(/^\[(.+)\]$/);
          if (arrayStyleMatch) {
            const innerContent = arrayStyleMatch[1];
            const items = innerContent
              .split(",")
              .map((item) => item.trim().replace(/^['"]|['"]$/g, ""));
            // Filter out "none" values and empty items
            return items.filter(
              (item) => item.length > 0 && item !== "none" && item !== "null"
            );
          }
        } catch {
          console.warn(`Failed to parse multiSelect value: ${value}`);
        }
      }
    }

    // Return filtered array, removing "none" values that represent null
    const arrayValue = Array.isArray(value)
      ? value
      : Array.isArray(config.defaultValue)
        ? config.defaultValue
        : [];
    return arrayValue.filter(
      (v) => v !== "none" && v !== "null" && v !== null && v !== undefined
    );
  }

  if (config.type === "dateRange") {
    return getParsedFieldValue(value, "dateRange");
  }

  if (config.type === "attachments" || config.type === "readOnlyAttachments") {
    return parseFileFieldValue(value);
  }

  return value;
};

const DetailFields = <T extends BaseEntityData>({
  entity,
  fieldsConfig,
  onUpdateField,
  className,
  containerClassName,
  tPrefix,
  isDrawerOpen = true,
  isQuickView = false,
  context,
  hideContainer = false,
  sections
}: Props<T>) => {
  const { t } = useTranslation();
  const isNewFieldsUI = useNewFieldsUI();
  const getFieldKey = useCallback(createGetFieldKey(entity), [entity?.id]);
  // For select/multiSelect: see createGetStableFieldKey — these are
  // popover-controlled and must not remount on every selection.
  const getStableFieldKey = useCallback(createGetStableFieldKey(entity), [
    entity?.id
  ]);

  // Empty preview rows read as an instruction naming their own field. Fields
  // that open a picker say "Select <field>"; everything else says
  // "Add <field>". Routing every row through these keeps the two phrasings
  // from drifting apart again, and the bare verb covers a field with no label.
  const selectPlaceholder = (label?: string) =>
    label ? `${t("select")} ${label}` : t("select");
  const addPlaceholder = (label?: string) =>
    label ? `${t("add")} ${label}` : t("add");
  const hasSortedRef = useRef(false);
  const previousOpenState = useRef(isDrawerOpen);

  const [fieldsWithError, setFieldsWithError] = useState<Set<string>>(
    new Set()
  );
  const [isEmptyFieldsOpen, setIsEmptyFieldsOpen] = useState(false);
  const [sortedFields, setSortedFields] = useState<{
    filledFields: Record<string, FieldItem>;
    emptyFields: Record<string, FieldItem>;
  }>({ filledFields: {}, emptyFields: {} });

  // State for clickableUrl fields - using a map to handle multiple url fields
  const [urlFieldValues, setUrlFieldValues] = useState<Record<string, string>>(
    {}
  );

  // Inline "add new" for partner / partner-contact entity fields
  const [entityCreateView, setEntityCreateView] = useState<
    null | "partner" | "partnerContact"
  >(null);
  const [entityCreateField, setEntityCreateField] = useState<string | null>(
    null
  );

  const closeEntityCreate = () => {
    setEntityCreateView(null);
    setEntityCreateField(null);
  };

  const handleEntityCreated = (id: string, name: string) => {
    if (!id || !name || !entityCreateField) {
      closeEntityCreate();
      return;
    }
    if (fieldsConfig[entityCreateField]?.type === "multiSelect") {
      const current = get(entity, entityCreateField);
      const existing = Array.isArray(current) ? current : [];
      onUpdateField(entityCreateField, [...existing, id]);
    } else {
      onUpdateField(entityCreateField, id);
    }
    closeEntityCreate();
  };

  useEffect(() => {
    hasSortedRef.current = false;
    setFieldsWithError(new Set());
    setSortedFields({ filledFields: {}, emptyFields: {} });
    setUrlFieldValues({});
  }, [entity?.id, fieldsConfig]);

  useEffect(() => {
    if (isQuickView) {
      // If drawer has just opened and we haven't sorted yet
      if (isDrawerOpen && !previousOpenState.current) {
        hasSortedRef.current = false;
      }
      previousOpenState.current = isDrawerOpen;
    }
  }, [isDrawerOpen, isQuickView]);

  const handleFieldError = (fieldName: string, hasError: boolean) => {
    setFieldsWithError((prev) => {
      const newSet = new Set(prev);
      if (hasError) {
        newSet.add(fieldName);
      } else {
        newSet.delete(fieldName);
      }
      return newSet;
    });
  };

  const getFieldValue = (fieldName: string, config: FieldConfig) => {
    if (!config) {
      return undefined;
    }

    const shouldStripHtml =
      config.type === "editableInput" ||
      config.type === "readOnlyText" ||
      config.type === "textWithHoverAction";

    // For linked fields without linkedDataInfo, check if entity has a _resolved field
    // and use that instead of the ID field
    if (
      (config.type === "readOnlyText" ||
        config.type === "readOnlyAttachments" ||
        config.type === "textWithHoverAction") &&
      config.fieldKey
    ) {
      // The API emits the companion field camelCased, matching the convention
      // the linked-field resolver uses. The literal suffix is kept as a
      // fallback so a payload that carries the snake_case form still resolves.
      const resolvedValue =
        get(entity, camelizeFieldKey(`${config.fieldKey}_resolved`)) ??
        get(entity, `${config.fieldKey}_resolved`);

      // If we have a resolved value, use it; otherwise fall back to the original field
      if (resolvedValue !== undefined && resolvedValue !== null) {
        const parsed = getParsedFieldValueForDetailFields(
          resolvedValue,
          config
        );
        return shouldStripHtml ? stripHtml(parsed) : parsed;
      }
    }

    const value = get(entity, fieldName);
    const parsed = getParsedFieldValueForDetailFields(value, config);
    return shouldStripHtml ? stripHtml(parsed) : parsed;
  };

  const renderFieldComponent = (fieldName: string, config: FieldConfig) => {
    // Guard against undefined config
    if (!config) {
      if (import.meta.env.DEV) {
        console.warn(`Field config is undefined for field: ${fieldName}`);
      }
      return null;
    }

    // This renderer feeds both row shells, so each inset names its
    // pre-redesign value too.
    const valueInset = (legacy: string) => (isNewFieldsUI ? "px-2" : legacy);

    const value = getFieldValue(fieldName, config);
    // Mirror the editableInput/textarea cases: keep the config-level disabled
    // authoritative — a disabled key inside additionalProps must not re-enable
    // a permission-locked field.
    const { disabled: additionalSelectDisabled, ...selectAdditionalProps } =
      config.additionalProps || {};
    switch (config.type) {
      case "select":
        if (config.isEntityField && config.displayType) {
          const partnerEntityCreateView =
            config.displayType === FieldDisplayType.PARTNERS
              ? "partner"
              : config.displayType === FieldDisplayType.PARTNER_CONTACTS
                ? "partnerContact"
                : null;
          const allowPartnerCreate =
            config.config?.allowUsersToAdd && !!partnerEntityCreateView;
          return (
            <EntitySelectField
              key={getStableFieldKey(fieldName as keyof T)}
              fieldName={fieldName}
              displayType={config.displayType}
              value={value?.toString() || null}
              onChange={(newValue) => {
                const additionalFields: Record<string, string | number> = {};
                (config.dependencies ?? []).forEach((dependency) => {
                  additionalFields[dependency] = null as unknown as string;
                });
                onUpdateField(fieldName, newValue, additionalFields);
              }}
              disabled={additionalSelectDisabled || config?.disabled}
              variant={(config.variant as singleSelectVariant) || "inline"}
              dependentValues={config?.dependentValues || {}}
              showFixedOptions={config?.showFixedOptions}
              customFixedOptions={config?.fixedOptions}
              excludeIds={config?.excludeIds}
              // Gated: base passed no placeholder here.
              placeholder={
                isNewFieldsUI ? selectPlaceholder(config.label) : undefined
              }
              onCreateNew={
                allowPartnerCreate && partnerEntityCreateView
                  ? () => {
                      setEntityCreateField(fieldName);
                      setEntityCreateView(partnerEntityCreateView);
                    }
                  : undefined
              }
              createNewLabel={
                allowPartnerCreate
                  ? getCreateNewLabel(config.displayType, t)
                  : undefined
              }
              {...selectAdditionalProps}
              // After the spread: EntitySelectField derives `clearable` from
              // `required`, so a config carrying `required` in additionalProps
              // could otherwise make a required entity field clearable. `||`,
              // not `??`: either source asserting the constraint wins, and a
              // config computing `required: !canClear` sends an explicit false
              // that `??` would let stand over the caller's true.
              required={config.required || selectAdditionalProps?.required}
            />
          );
        }

        // Add entity creation functionality for entity-based single select fields
        let singleSelectEntityProps = {};
        if (config.isEntityField && config.displayType && context) {
          singleSelectEntityProps = {
            onCreateNew: () => {
              if (config.displayType === "companies") {
                context.setView?.("companies");
              } else if (config.displayType === "people") {
                context.setView?.("people");
              }
            },
            createNewLabel: getCreateNewLabel(config.displayType, t),
            customOnChange:
              config.displayType === "companies"
                ? context.onCompanySelect
                : config.displayType === "products"
                  ? context.onProductSelect
                  : undefined
          };
        }

        // Add create new option functionality for non-entity single select fields
        const shouldShowCreateNew = config.allowUsersToAdd && context;
        const fieldKey = fieldName;
        const fieldId = config.fieldId || fieldName;

        let popOverFooter;
        if (shouldShowCreateNew) {
          popOverFooter = (searchTerm: string) => {
            const showButton = shouldShowCreateNewButton(searchTerm);
            if (!showButton) {
              return <div style={{ display: "none" }} />;
            }

            const isDisabled = shouldDisableCreateNewButton(
              searchTerm,
              config.options
            );

            return (
              <AddButtonFooter
                buttonText={t(
                  "settings.userManagement.addNewUserModal.createNewOption"
                )}
                onClick={createAddNewOptionHandler(
                  context,
                  fieldId,
                  fieldKey,
                  searchTerm
                )}
                variant="iconNoBg"
                disabled={isDisabled}
              />
            );
          };
        }

        return (
          <SingleSelect
            key={getStableFieldKey(fieldName as keyof T)}
            fieldName={fieldName}
            options={config.options}
            selectedValue={value}
            onSelection={(newValue) => onUpdateField(fieldName, newValue)}
            formatter={config.formatter}
            renderIcon={config.renderOptionIcon}
            entity={config.entity}
            showSearch={config?.showSearch || !!shouldShowCreateNew}
            fixedOptions={config?.fixedOptions}
            popoverClassName={config?.popoverClassName}
            treatNullAsValue={config?.treatNullAsValue}
            disabled={additionalSelectDisabled || config?.disabled}
            disabledTooltipText={config?.disabledTooltipText}
            initialSearchTerm={config?.searchTerm}
            onSearch={config?.onSearch}
            isLoading={config?.isLoading}
            placeholder={selectPlaceholder(config.label)}
            className="h-9"
            mainContainerClassName="!shadow-none"
            popOverFooter={popOverFooter}
            {...singleSelectEntityProps}
            {...selectAdditionalProps}
            // After both spreads, and `onClear` is withheld rather than merely
            // paired with `clearable={false}`: the chip variant ignores
            // `clearable` and derives its remove control from Boolean(onClear),
            // so a required field would still offer one.
            clearable={!config.required}
            onClear={
              config.required ? undefined : () => onUpdateField(fieldName, "")
            }
          />
        );

      case "editableInput": {
        // Mirror the textarea case: destructure disabled out of
        // additionalProps so the permission-derived lock can't be spread over.
        const { disabled: additionalDisabled, ...otherSingleInputProps } =
          config.additionalProps || {};
        const rawInitial =
          value?.toString() ?? config.initialValue?.toString() ?? "";
        const initialValue = config.isPercentage
          ? (toPercentInput(rawInitial)?.toString() ?? "")
          : rawInitial;
        const handleSave = (newValue: string) => {
          if (!config.isPercentage) {
            onUpdateField(fieldName, newValue);
            return;
          }
          const stored = fromPercentInput(newValue);
          onUpdateField(fieldName, stored === null ? "" : stored);
        };
        const editableInputField = (
          <div className={cn("flex items-center", isNewFieldsUI && "w-full")}>
            <SingleInput
              key={getFieldKey(fieldName as keyof T)}
              fieldName={fieldName}
              initialValue={initialValue}
              onSave={handleSave}
              textVariant="body"
              validation={config.validation}
              isMonetary={config.isMonetary}
              currencySymbol={config.currencySymbol}
              isPretty={config.isPretty}
              onError={(error) => handleFieldError(fieldName, !!error)}
              placeholder={addPlaceholder(config.label)}
              // The redesigned rows sit the input flush in the row rather than
              // in a boxed field; the pre-redesign rows keep the boxed look.
              variant={isNewFieldsUI ? "inline" : undefined}
              isPercentage={config.isPercentage}
              isEmail={config.additionalProps?.type === "email"}
              disabled={additionalDisabled || config.disabled}
              {...otherSingleInputProps}
              // After the spread: additionalProps is caller-supplied and
              // unrestricted, so spreading over this let a required field
              // offer a clear control.
              showClearButton={!config.required}
            />
            {(!isNewFieldsUI || !config.isPercentage) && config.suffixComponent}
          </div>
        );
        if (config.disabled && config.disabledTooltipText) {
          return (
            <Tooltip>
              <TooltipTrigger asChild>{editableInputField}</TooltipTrigger>
              <TooltipContent>{config.disabledTooltipText}</TooltipContent>
            </Tooltip>
          );
        }
        return editableInputField;
      }
      case "editableMultiInput":
        return (
          <MultiInput
            key={getFieldKey(fieldName as keyof T)}
            fieldName={fieldName}
            initialValue={value || config.initialValue || []} // Pass the actual array value
            onSave={(newValue: string[]) =>
              onUpdateField(fieldName, newValue as any)
            }
            addNewText={t("people.addEmail")}
            textVariant="body"
            validation={config.validation}
            isMonetary={config.isMonetary}
            isPretty={config.isPretty}
            currencySymbol={config.currencySymbol}
            onError={(error) => handleFieldError(fieldName, !!error)}
            maxEntries={config.additionalProps?.maxEntries}
            // Gated: base had no fallback beyond additionalProps.
            placeholder={
              isNewFieldsUI
                ? (config.additionalProps?.placeholder ??
                  addPlaceholder(config.label))
                : config.additionalProps?.placeholder
            }
            allowAddNew={config.additionalProps?.allowAddNew}
            {...config.additionalProps}
            // After the spread, like the editableInput case: config.disabled is
            // the permission-derived lock and never reached this control at all.
            // `||` to match the select/editableInput/textarea cases: either lock
            // holds, and a config computing `disabled: !canEdit` sends an
            // explicit false that `??` would let unlock the control.
            disabled={config.disabled || config.additionalProps?.disabled}
            // Also after: MultiInput derives canRemove from required, so a
            // config carrying required inside additionalProps could otherwise
            // let the last entry of a required field be cleared. `||` for the
            // same reason as the disabled line above.
            required={config.required || config.additionalProps?.required}
          />
        );
      case "date":
        return (
          <DateSelection
            key={getFieldKey(fieldName as keyof T)}
            date={value ? toFieldDay(value, config.localDay) : null}
            onDateSelection={(newDate: Date) =>
              onUpdateField(fieldName, toDateOnlyISO(newDate))
            }
            // Gated: base passed no placeholder to DateSelection.
            placeholder={
              isNewFieldsUI ? selectPlaceholder(config.label) : undefined
            }
            {...config.additionalProps}
            // After the spread, like the sibling cases above: config.disabled is
            // the permission-derived lock, and `||` so an explicit false from
            // either source cannot unlock a field the other one locked.
            disabled={config.disabled || config.additionalProps?.disabled}
            // `??` rather than `||` for the rest — a string and two dates,
            // where only genuine absence should fall through to the spread.
            disabledTooltipText={
              config.disabledTooltipText ??
              config.additionalProps?.disabledTooltipText
            }
            availableFrom={
              config.availableFrom ?? config.additionalProps?.availableFrom
            }
            availableTo={
              config.availableTo ?? config.additionalProps?.availableTo
            }
            // After the spread: additionalProps is arbitrary, and an onClear
            // arriving through it would hand a required date a clear control.
            onClear={
              !config.required || config.clearable
                ? () => onUpdateField(fieldName, "")
                : undefined
            }
          />
        );

      case "readOnlyDate":
        return (
          <Text
            key={getFieldKey(fieldName as keyof T)}
            variant="body"
            className={valueInset("pl-1 px-3")}
            data-test={fieldName}
          >
            {value
              ? simpleDate(toFieldDay(value, config.localDay), "long")
              : (config.emptyText ?? t("unknown"))}
          </Text>
        );

      case "scoreBar":
        return value ? (
          <div
            key={getFieldKey(fieldName as keyof T)}
            className={cn("flex items-center", valueInset("pl-1 px-3"))}
          >
            <Text variant="body" className="mr-2">
              {value.toString()}
              {config.suffix}
            </Text>
            <div className="w-24 bg-gray-600 rounded-full h-1">
              <Progress
                value={value}
                levels={config.levels}
                indicatorClassname="border-r border-surface-elevation1"
              />
            </div>
          </div>
        ) : (
          <Text variant="body" className={valueInset("pl-1 px-3")}>
            {t("unknown")}
          </Text>
        );

      case "strengthBar":
        if (!value)
          return (
            <Text variant="body" className={valueInset("pl-1 px-3")}>
              {t("unknown")}
            </Text>
          );

        const scoreStrength = ScoreStrengthFromValue(
          value,
          config?.scoreStrengthThresholds
        );
        return (
          <div
            key={getFieldKey(fieldName as keyof T)}
            className={cn(
              "flex items-center h-6 overflow-hidden",
              valueInset("pl-3 px-3")
            )}
          >
            <ScoreBars score={scoreStrength as ScoreStrength} />
            <Text variant="body" className="ml-2">
              {config.getLabel(scoreStrength as ScoreStrength)}
            </Text>
          </div>
        );

      case "readOnlyText":
        return (
          <Text
            key={getFieldKey(fieldName as keyof T)}
            variant="body"
            className={valueInset("pl-1 px-3")}
          >
            {config.formatter
              ? config.formatter(value)
              : (value ?? t("unknown"))}
          </Text>
        );

      case "link": {
        const displayText = config.formatter
          ? config.formatter(value)
          : (value ?? t("unknown"));
        const linkClassName = cn(
          valueInset("pl-1 px-3"),
          "text-link hover:underline"
        );
        return config.to ? (
          <Link
            key={getFieldKey(fieldName as keyof T)}
            to={config.to}
            data-test={fieldName}
          >
            <Text variant="body" className={linkClassName}>
              {displayText}
            </Text>
          </Link>
        ) : (
          <Text
            key={getFieldKey(fieldName as keyof T)}
            variant="body"
            className={valueInset("pl-1 px-3")}
          >
            {displayText}
          </Text>
        );
      }

      case "linkedField":
        let linkedFieldDisplayText: string;
        if (config.formatter) {
          linkedFieldDisplayText = config.formatter(value);
        } else if (config.displayType === "multiSelect") {
          const formattedValue = formatMultiSelectValueForDisplay(value);
          linkedFieldDisplayText = formattedValue || t("unknown");
        } else {
          linkedFieldDisplayText = value ?? t("unknown");
        }

        return (
          <TextWithEditLink
            text={linkedFieldDisplayText}
            linkedEntity={config.linkedEntity}
            linkedEntityType={config.linkedEntityType}
            onUpdate={config?.onUpdate}
            className={valueInset("px-3")}
          />
        );

      case "textWithHoverAction":
        return (
          <TextWithHoverAction
            text={
              config.formatter
                ? config.formatter(value)
                : String(value ?? t("unknown"))
            }
            actionLabel={config.actionLabel}
            actionAriaLabel={config.actionAriaLabel}
            actionDataTest={config.actionDataTest ?? `${fieldName}-action`}
            to={config.to}
            replace={config.replace}
            onAction={config.onAction}
            className={valueInset("px-3")}
          />
        );

      case "switch":
        const switchOptions = {
          [config.options?.[0]?.id || "option1"]:
            config.options?.[0]?.displayName || "Option 1",
          [config.options?.[1]?.id || "option2"]:
            config.options?.[1]?.displayName || "Option 2"
        };
        const toggleProps = {
          options: switchOptions,
          defaultValue:
            value ||
            config.defaultValue ||
            config.options?.[0]?.id ||
            "option1",
          valueSelected: (selectedValue: string) =>
            onUpdateField(fieldName, selectedValue)
        };
        return (
          // Tabs already insets its own label text (frame p-[2px] + button
          // px-2), the same way TextEditor's placeholder already sits at its
          // own px-2. Wrapping it in another px-2 here stacked a second inset
          // on top, same bug as the textarea case above — drop it and let
          // Tabs' own spacing stand.
          <div
            key={getFieldKey(fieldName as keyof T)}
            className={isNewFieldsUI ? undefined : "px-3"}
          >
            <Tabs size="lg" {...toggleProps} />
          </div>
        );

      case "checkbox":
        return (
          <div
            key={getFieldKey(fieldName as keyof T)}
            className="flex items-center"
          >
            <SingleCheckbox
              id={`checkbox-${fieldName}`}
              label={config.label || t(`${tPrefix}.${fieldName}`)}
              isChecked={decodeCheckbox(value)}
              onCheckedChange={(checked) =>
                onUpdateField(fieldName, checked as boolean)
              }
              {...config.additionalProps}
            />
          </div>
        );

      case "checklistItem":
        return (
          <div
            key={getFieldKey(fieldName as keyof T)}
            className="flex items-center"
          >
            <Checkmark
              id={`checklist-item-${fieldName}`}
              label={decodeCheckbox(value) ? t("completed") : t("notCompleted")}
              checked={decodeCheckbox(value)}
              onCheckedChange={(checked) => onUpdateField(fieldName, checked)}
            />
          </div>
        );

      case "textarea":
        const { disabled, ...otherAdditionalProps } =
          config.additionalProps || {};
        return (
          <div
            key={getFieldKey(fieldName as keyof T)}
            className={cn("w-full", !isNewFieldsUI && "px-3")}
          >
            <div className="w-full min-w-0">
              <TextEditor
                value={
                  value?.toString() ?? config.initialValue?.toString() ?? ""
                }
                onBlur={(newValue: string) =>
                  onUpdateField(fieldName, newValue)
                }
                // Gated: base rendered the literal "Enter text..." here.
                placeholder={
                  isNewFieldsUI ? addPlaceholder(config.label) : "Enter text..."
                }
                disabled={disabled || config.disabled}
                variant="inline"
                {...otherAdditionalProps}
              />
            </div>
          </div>
        );

      case "multiSelect": {
        const transformedOptions = config.options.map((option: any) => ({
          label: option.displayName || option.label || option.name || option,
          value: option.id || option.value || option
        }));

        // Filter out "none" values when they represent empty state
        const filteredValue = Array.isArray(value)
          ? value.filter(
              (v) =>
                v !== "none" && v !== "null" && v !== null && v !== undefined
            )
          : [];

        const handleMultiSelectChange = (newValues: string[]) => {
          // If no values selected, set to empty array instead of ["none"]
          const processedValues = newValues.length === 0 ? [] : newValues;
          if (config.customOnChange) {
            config.customOnChange(processedValues);
          }
          onUpdateField(fieldName, processedValues);
        };

        const multiSelectKey = getStableFieldKey(fieldName as keyof T);

        if (config.isEntityField && config.displayType) {
          const allowUsersToAdd = config.config?.allowUsersToAdd;
          return (
            <EntityMultiSelectField
              key={multiSelectKey}
              fieldName={fieldName}
              displayType={config.displayType}
              value={filteredValue}
              onChange={(newValues) => onUpdateField(fieldName, newValues)}
              dependentValues={config?.dependentValues || {}}
              config={config.config}
              disabled={config?.disabled}
              variant="inline"
              className="w-full"
              // Gated: base passed no placeholder here.
              placeholder={
                isNewFieldsUI ? selectPlaceholder(config.label) : undefined
              }
              onCreateNew={
                allowUsersToAdd
                  ? () => {
                      setEntityCreateField(fieldName);
                      setEntityCreateView("partnerContact");
                    }
                  : undefined
              }
              createNewLabel={
                allowUsersToAdd
                  ? getCreateNewLabel(config.displayType, t)
                  : undefined
              }
            />
          );
        }

        // Add entity creation functionality for entity-based multiSelect fields
        let entityProps = {};
        if (config.isEntityField && config.displayType && context) {
          entityProps = {
            onCreateNew: () => {
              if (config.displayType === "companies") {
                context.setView?.("companies");
              } else if (config.displayType === "people") {
                context.setView?.("people");
              }
            },
            createNewLabel: getCreateNewLabel(config.displayType, t),
            customOnChange:
              config.displayType === "companies"
                ? context.onCompanySelect
                : config.displayType === "products"
                  ? context.onProductSelect
                  : undefined
          };
        }

        // Add create new option functionality for non-entity multiSelect fields
        const shouldShowCreateNewMulti = config.allowUsersToAdd && context;
        const fieldKeyMulti = fieldName;
        const fieldIdMulti = config.fieldId || fieldName;

        let popOverFooterMulti;
        if (shouldShowCreateNewMulti) {
          popOverFooterMulti = (searchTerm: string) => {
            const showButton = shouldShowCreateNewButton(searchTerm);
            if (!showButton) {
              return <div style={{ display: "none" }} />;
            }

            const isDisabled = shouldDisableCreateNewButton(
              searchTerm,
              transformedOptions
            );

            return (
              <AddButtonFooter
                buttonText={t(
                  "settings.userManagement.addNewUserModal.createNewOption"
                )}
                onClick={createAddNewOptionHandler(
                  context,
                  fieldIdMulti,
                  fieldKeyMulti,
                  searchTerm
                )}
                variant="iconNoBg"
                disabled={isDisabled}
              />
            );
          };
        }

        return (
          <MultiSelect
            key={multiSelectKey}
            options={transformedOptions}
            selectedValues={filteredValue}
            onValueChange={handleMultiSelectChange}
            variant="inline"
            placeholder={selectPlaceholder(config.label)}
            // MultiSelect already sets its own closed (h-9) vs open (min-h-9)
            // height. A fixed h-9 here used to win over that min-h-9 once the
            // popover opened with many chips selected — the trigger couldn't
            // grow, so wrapped chips spilled out past its bottom edge.
            className="w-full"
            showSearch={config?.showSearch || !!shouldShowCreateNewMulti}
            onSearch={config?.onSearch}
            isLoading={config?.isLoading}
            searchPlaceholder={config?.searchPlaceholder}
            isEntityField={config.isEntityField}
            displayType={config.displayType}
            config={config.config}
            popOverFooter={popOverFooterMulti}
            {...entityProps}
            {...config.additionalProps}
            // After the spread, like the cases above: config.disabled is the
            // permission-derived lock, and a disabled key inside
            // additionalProps must not re-enable it. `||` for the same reason
            // as the multiInput case: an explicit false must not unlock it.
            disabled={config.disabled || config.additionalProps?.disabled}
          />
        );
      }

      case "dateRange":
        return (
          <DateRangeField
            key={getFieldKey(fieldName as keyof T)}
            value={value}
            onUpdate={(dateRangeValue) => {
              // Convert the date range object to JSON string for storage
              try {
                const jsonString = JSON.stringify(dateRangeValue);
                onUpdateField(fieldName, jsonString);
              } catch (error) {
                console.error(`Failed to stringify dateRange value:`, error);
                // Fallback to original object if JSON.stringify fails
                onUpdateField(fieldName, dateRangeValue);
              }
            }}
            fieldKey={getFieldKey(fieldName as keyof T)}
            disabled={config.disabled}
            // Gated: base had no fallback beyond additionalProps.
            placeholder={
              isNewFieldsUI
                ? (config.additionalProps?.placeholder ??
                  selectPlaceholder(config.label))
                : config.additionalProps?.placeholder
            }
            variant="inline"
            onClear={
              !config.required ? () => onUpdateField(fieldName, "") : undefined
            }
          />
        );

      case "readOnlyAttachments": {
        // Resolved once: the padding override and the row that compensates for
        // it must never disagree, and the two config fields are independently
        // optional, so reading them separately would let one fire without the
        // other.
        const linkedSource =
          config.linkedEntity && config.linkedEntityType
            ? {
                entity: config.linkedEntity,
                entityType: config.linkedEntityType
              }
            : undefined;

        const readOnlyAttachmentsField = (
          <ReadOnlyAttachmentsField
            attachments={
              config.attachmentsResolver
                ? config.attachmentsResolver(value)
                : parseFileFieldValue(value)
            }
            name={fieldName}
            emptyText={config.emptyText}
            // The edit-action row owns the horizontal padding when there is one.
            className={linkedSource ? "px-0" : undefined}
          />
        );

        // A file value living on a linked entity is edited there, so the row
        // carries the same action a linked text value does. The chips stay
        // read-only either way — only the route to the source is added.
        return linkedSource ? (
          <TextWithEditLink
            key={getFieldKey(fieldName as keyof T)}
            content={readOnlyAttachmentsField}
            linkedEntity={linkedSource.entity}
            linkedEntityType={linkedSource.entityType}
            className={valueInset("px-3")}
          />
        ) : (
          <React.Fragment key={getFieldKey(fieldName as keyof T)}>
            {readOnlyAttachmentsField}
          </React.Fragment>
        );
      }

      case "attachments":
        return (
          <DetailAttachmentsField
            key={getFieldKey(fieldName as keyof T)}
            // Same composite as the registry renderer and the form — see
            // FileUploadControl for why "preview" is wrong on a record field.
            variant="edit"
            value={parseFileFieldValue(value)}
            onChange={(attachments) =>
              onUpdateField(
                fieldName,
                serializeFileFieldValue(attachments || [])
              )
            }
            name={fieldName}
            // Gated: base had no fallback beyond additionalProps.
            placeholder={
              isNewFieldsUI
                ? (config.additionalProps?.placeholder ??
                  addPlaceholder(config.label))
                : config.additionalProps?.placeholder
            }
            accept={config.accept}
            isSingleFile={config.isSingleFile}
            disabled={config.disabled}
          />
        );

      case "clickableUrl":
        const currentUrlValue =
          urlFieldValues[fieldName] ??
          value?.toString() ??
          config.initialValue?.toString() ??
          "";

        const handleUrlChange = (newValue: string) => {
          setUrlFieldValues((prev) => ({
            ...prev,
            [fieldName]: newValue
          }));

          if (config.onSave) {
            config.onSave(newValue);
          }
          onUpdateField(fieldName, newValue);
        };

        return (
          <ClickableUrlField
            key={getFieldKey(fieldName as keyof T)}
            value={currentUrlValue}
            onChange={handleUrlChange}
            // Gated: the pre-redesign row prompts to add a link only when no
            // placeholder is given — a read-only field's empty one stays empty.
            placeholder={
              isNewFieldsUI
                ? (config.additionalProps?.placeholder ??
                  addPlaceholder(config.label))
                : (config.additionalProps?.placeholder ?? t("addLink"))
            }
            readOnly={config.readOnly}
          />
        );

      case "button":
        return (
          <ButtonField
            key={getFieldKey(fieldName as keyof T)}
            config={config.config}
            entityId={entity?.id || ""}
            entity={entity as Record<string, unknown> | undefined}
            label={config.label}
            onSuccess={config.onSuccess}
            onError={config.onError}
            {...config.additionalProps}
          />
        );

      case "currencySelect":
        return (
          <CurrencySelectField
            key={getFieldKey(fieldName as keyof T)}
            fieldName={fieldName}
            value={value?.toString() || null}
            onChange={(newValue) => onUpdateField(fieldName, newValue)}
            placeholder={
              isNewFieldsUI
                ? (config.placeholder ?? selectPlaceholder(config.label))
                : config.placeholder
            }
            disabled={config.disabled}
            variant={isNewFieldsUI ? "inline" : "default"}
          />
        );

      case "phone":
        return (
          <PhoneControl
            key={getFieldKey(fieldName as keyof T)}
            fieldType={FieldDisplayType.PHONE}
            name={fieldName}
            value={value ?? ""}
            onChange={(newValue) => {
              const valueToSave = getPhoneValueToSave(
                newValue,
                value ?? "",
                !!config.required
              );
              if (valueToSave !== null) onUpdateField(fieldName, valueToSave);
            }}
            variant={isNewFieldsUI ? "inline" : "form"}
            wrapperClassName="w-full"
            placeholder={addPlaceholder(config.label)}
            disabled={config.disabled}
            required={!!config.required}
          />
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    if (isDrawerOpen && isQuickView && !hasSortedRef.current) {
      const filledFieldsResult: Record<string, FieldItem> = {};
      const emptyFieldsResult: Record<string, FieldItem> = {};

      const fieldStates: Record<string, boolean> = {};
      forEach(fieldsConfig, (config, fieldName) => {
        const value = getFieldValue(fieldName, config);
        const isEmpty = isEmptyFieldValue(
          value,
          config.type,
          config.type === "select" ? config.treatNullAsValue : undefined
        );
        fieldStates[fieldName] = isEmpty;
      });

      forEach(fieldsConfig, (config, fieldName) => {
        if (config.layout === "half" && config.pairedWith) {
          const pairedFieldName = config.pairedWith;

          const primaryIsFilled = !fieldStates[fieldName];
          fieldStates[pairedFieldName] = !primaryIsFilled;
        }
      });

      forEach(fieldsConfig, (config, fieldName) => {
        const fieldItem = {
          component: renderFieldComponent(fieldName, config),
          icon: config.icon || ""
        };

        const isFieldEmpty = fieldStates[fieldName];
        if (config.keepOrder || !isFieldEmpty) {
          filledFieldsResult[fieldName] = fieldItem;
        } else {
          emptyFieldsResult[fieldName] = fieldItem;
        }
      });

      setSortedFields({
        filledFields: filledFieldsResult,
        emptyFields: emptyFieldsResult
      });

      hasSortedRef.current = true;
    }
  }, [isDrawerOpen, isQuickView, entity, fieldsConfig]);

  const detailsFields = useMemo(() => {
    if (!isQuickView) {
      return mapValues(fieldsConfig, (config, fieldName) => ({
        component: renderFieldComponent(fieldName, config),
        icon: config.icon || ""
      }));
    }

    if (!hasSortedRef.current || !isDrawerOpen) {
      return {};
    }

    // Update components for existing sorted fields without changing the order
    const updatedFilledFields = mapValues(
      sortedFields.filledFields,
      (item, fieldName) => {
        const config = fieldsConfig[fieldName];
        if (!config) {
          return { ...item, component: null };
        }
        return {
          ...item,
          component: renderFieldComponent(fieldName, config)
        };
      }
    );

    const updatedEmptyFields = mapValues(
      sortedFields.emptyFields,
      (item, fieldName) => {
        const config = fieldsConfig[fieldName];
        if (!config) {
          return { ...item, component: null };
        }
        return {
          ...item,
          component: renderFieldComponent(fieldName, config)
        };
      }
    );

    return {
      ...updatedFilledFields,
      ...updatedEmptyFields
    };
    // renderFieldComponent reads isNewFieldsUI, and the flag resolves a tick
    // after mount. Without it here the rows built while it was still false are
    // cached and never rebuilt, so the redesign never reaches them.
  }, [
    entity,
    fieldsConfig,
    sortedFields,
    hasSortedRef.current,
    isDrawerOpen,
    isQuickView,
    isNewFieldsUI
  ]);

  const filledFields = useMemo(() => {
    if (!isQuickView || !hasSortedRef.current) return {};

    return mapValues(sortedFields.filledFields, (item, fieldName) => {
      const config = fieldsConfig[fieldName];
      if (!config) {
        return { ...item, component: null };
      }
      return {
        ...item,
        component: renderFieldComponent(fieldName, config)
      };
    });
  }, [
    entity,
    fieldsConfig,
    sortedFields,
    hasSortedRef.current,
    isQuickView,
    isNewFieldsUI
  ]);

  const emptyFields = useMemo(() => {
    if (!isQuickView || !hasSortedRef.current) return {};

    return mapValues(sortedFields.emptyFields, (item, fieldName) => {
      const config = fieldsConfig[fieldName];
      if (!config) {
        return { ...item, component: null };
      }
      return {
        ...item,
        component: renderFieldComponent(fieldName, config)
      };
    });
  }, [
    entity,
    fieldsConfig,
    sortedFields,
    hasSortedRef.current,
    isQuickView,
    isNewFieldsUI
  ]);

  // The layout facts a row needs, derived once so the two row renderings can
  // never disagree about how tall a field is.
  const getRowLayout = (
    fieldName: string,
    allFields?: Record<string, FieldItem>
  ) => {
    const fieldConfig = fieldsConfig[fieldName];
    const growsForType = (type?: FieldType) =>
      type === "multiSelect" ||
      type === "attachments" ||
      type === "readOnlyAttachments";
    const pairedFieldName = fieldConfig?.pairedWith;
    const pairedFieldConfig = pairedFieldName
      ? fieldsConfig[pairedFieldName]
      : null;

    // A pair shares one value row, so every fact that sets the row's height has
    // to consider both halves — pairGrowsWithContent already did, while the
    // taller textarea/multi-input shapes and the error state read this half
    // alone and clipped a partner that needed the room.
    const isEitherHalf = (type: FieldType) =>
      fieldConfig?.type === type || pairedFieldConfig?.type === type;

    return {
      fieldConfig,
      isMultiInput: isEitherHalf("editableMultiInput"),
      isTextarea: isEitherHalf("textarea"),
      growsWithContent: growsForType(fieldConfig?.type),
      pairGrowsWithContent:
        growsForType(fieldConfig?.type) ||
        growsForType(pairedFieldConfig?.type),
      hasError:
        fieldsWithError.has(fieldName) ||
        Boolean(pairedFieldName && fieldsWithError.has(pairedFieldName)),
      isHalfLayout: fieldConfig?.layout === "half",
      pairedFieldConfig,
      pairedFieldItem:
        pairedFieldName && allFields ? allFields[pairedFieldName] : null
    };
  };

  // A field that is the second half of a pair is rendered by its partner.
  const isPairedPartner = (
    fieldName: string,
    allFields?: Record<string, FieldItem>
  ) =>
    Boolean(
      allFields &&
        Object.entries(fieldsConfig).some(
          ([name, config]) =>
            config.layout === "half" &&
            config.pairedWith === fieldName &&
            allFields[name]
        )
    );

  const renderNewFieldRow = (
    fieldName: string,
    fieldItem: FieldItem,
    allFields?: Record<string, FieldItem>
  ) => {
    const {
      fieldConfig,
      isMultiInput,
      isTextarea,
      growsWithContent,
      pairGrowsWithContent,
      hasError,
      isHalfLayout,
      pairedFieldConfig,
      pairedFieldItem
    } = getRowLayout(fieldName, allFields);

    if (!fieldConfig) {
      return null;
    }

    const label =
      fieldConfig.label !== undefined
        ? fieldConfig.label
        : t(`${tPrefix}.${fieldName}`);

    if (isHalfLayout && pairedFieldItem && pairedFieldConfig) {
      return (
        <DetailFieldRow
          key={fieldName}
          label={label}
          icon={fieldItem.icon}
          helpText={fieldConfig.helpText}
          hasError={hasError}
          paired
          isTextarea={isTextarea}
          isMultiInput={isMultiInput}
          growsWithContent={pairGrowsWithContent}
        >
          <div className="flex-1">{fieldItem.component}</div>
          <div className="flex-1">{pairedFieldItem.component}</div>
        </DetailFieldRow>
      );
    }

    if (isPairedPartner(fieldName, allFields)) {
      return null;
    }

    return (
      <DetailFieldRow
        key={fieldName}
        label={label}
        icon={fieldItem.icon}
        helpText={fieldConfig.helpText}
        hasError={hasError}
        isTextarea={isTextarea}
        isMultiInput={isMultiInput}
        growsWithContent={growsWithContent}
      >
        {fieldItem.component}
      </DetailFieldRow>
    );
  };

  const renderLegacyFieldRow = (
    fieldName: string,
    fieldItem: FieldItem,
    allFields?: Record<string, FieldItem>
  ) => {
    const fieldConfig = fieldsConfig[fieldName];

    if (!fieldConfig) {
      return null;
    }

    const isMultiInput = fieldConfig.type === "editableMultiInput";
    const isTextarea = fieldConfig.type === "textarea";
    const isMultiSelect = fieldConfig.type === "multiSelect";
    // An attachments field lists one item per file, so it grows the same way a
    // multiSelect does — held at the default fixed row height its second file
    // is clipped instead of shown.
    const isAttachments =
      fieldConfig.type === "attachments" ||
      fieldConfig.type === "readOnlyAttachments";
    const growsWithContent = isMultiSelect || isAttachments;
    const hasError = fieldsWithError.has(fieldName);
    const isHalfLayout = fieldConfig.layout === "half";
    const pairedFieldName = fieldConfig.pairedWith;
    const pairedFieldConfig = pairedFieldName
      ? fieldsConfig[pairedFieldName]
      : null;
    const pairedFieldItem =
      pairedFieldName && allFields ? allFields[pairedFieldName] : null;
    // A pair shares one value row, so a growing field in either half has to set
    // that row's height — sized off this half alone, the partner's chips are
    // clipped.
    const pairGrowsWithContent =
      growsWithContent ||
      pairedFieldConfig?.type === "multiSelect" ||
      pairedFieldConfig?.type === "attachments" ||
      pairedFieldConfig?.type === "readOnlyAttachments";

    // If this field is paired with another field and has half layout, render them together
    if (isHalfLayout && pairedFieldItem && pairedFieldConfig) {
      return (
        <div key={fieldName} className="group contents">
          <div
            className={cn(
              "flex gap-2 pr-4 h-9",
              isTextarea || pairGrowsWithContent
                ? "items-start pt-2 overflow-visible"
                : "overflow-hidden",
              hasError || isMultiInput || isTextarea || pairGrowsWithContent
                ? "items-start pt-2"
                : "items-center"
            )}
          >
            {IconsMap?.[fieldItem.icon] && (
              <IconContainer size="sm" color="secondary">
                {IconsMap[fieldItem.icon]}
              </IconContainer>
            )}
            <div className="flex items-center gap-1 min-w-0 flex-1">
              {fieldConfig.label !== "" && (
                <Text
                  variant="bodySecondary"
                  className="truncate"
                  hasTruncatedTooltip
                >
                  {fieldConfig.label !== undefined
                    ? fieldConfig.label
                    : t(`${tPrefix}.${fieldName}`)}
                </Text>
              )}
              {fieldConfig.helpText && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <IconContainer size="sm" color="secondary">
                        <InfoCircle />
                      </IconContainer>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent maxWidth={13}>
                    <Text variant="smBody">{fieldConfig.helpText}</Text>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
          <div
            className={cn(
              "flex break-words gap-2",
              "w-[80%]",
              isTextarea
                ? "h-fit items-start min-h-[6rem] overflow-visible"
                : isMultiInput
                  ? "h-fit items-start min-h-[2rem] overflow-visible"
                  : pairGrowsWithContent
                    ? "h-fit items-start min-h-9 overflow-visible"
                    : hasError
                      ? "h-16 items-start overflow-hidden"
                      : "h-9 items-center overflow-hidden"
            )}
          >
            <div className="flex-1">{fieldItem.component}</div>
            <div className="flex-1">{pairedFieldItem.component}</div>
          </div>
        </div>
      );
    }

    // Skip rendering if this field is the paired field (it's already rendered with its pair)
    if (
      allFields &&
      Object.entries(fieldsConfig).some(
        ([name, config]) =>
          config.layout === "half" &&
          config.pairedWith === fieldName &&
          allFields[name]
      )
    ) {
      return null;
    }

    return (
      <div key={fieldName} className="group contents">
        <div
          className={cn(
            "flex gap-2 pr-4 h-9",
            isTextarea || growsWithContent
              ? "items-start pt-2 overflow-visible"
              : "overflow-hidden",
            hasError || isMultiInput || isTextarea || growsWithContent
              ? "items-start pt-2"
              : "items-center"
          )}
        >
          {IconsMap?.[fieldItem.icon] && (
            <IconContainer size="sm" color="secondary">
              {IconsMap[fieldItem.icon]}
            </IconContainer>
          )}
          <div className="flex items-center gap-1 min-w-0 flex-1">
            {fieldConfig.label !== "" && (
              <Text
                variant="bodySecondary"
                className="truncate"
                hasTruncatedTooltip
              >
                {fieldConfig.label !== undefined
                  ? fieldConfig.label
                  : t(`${tPrefix}.${fieldName}`)}
              </Text>
            )}
            {fieldConfig.helpText && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <IconContainer size="sm" color="secondary">
                      <InfoCircle />
                    </IconContainer>
                  </div>
                </TooltipTrigger>
                <TooltipContent maxWidth={13}>
                  <Text variant="smBody">{fieldConfig.helpText}</Text>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
        <div
          className={cn(
            "flex break-words",
            "w-[80%]",
            // Different height and alignment handling for different field types
            isTextarea
              ? "h-fit items-start min-h-[6rem] overflow-visible" // Allow dropdown to show, remove overflow-hidden
              : isMultiInput
                ? "h-fit items-start min-h-[2rem] overflow-visible" // Allow flexible height for multiInput
                : growsWithContent
                  ? "h-fit items-start min-h-9 overflow-visible"
                  : hasError
                    ? "h-16 items-start overflow-hidden"
                    : "h-9 items-center overflow-hidden"
          )}
        >
          {fieldItem.component}
        </div>
      </div>
    );
  };

  const renderField = (
    fieldName: string,
    fieldItem: FieldItem,
    allFields?: Record<string, FieldItem>
  ) =>
    isNewFieldsUI
      ? renderNewFieldRow(fieldName, fieldItem, allFields)
      : renderLegacyFieldRow(fieldName, fieldItem, allFields);

  const renderFieldsContainer = (
    fields: Record<string, FieldItem>,
    additionalClasses?: string,
    allFieldsForPairing?: Record<string, FieldItem>
  ) => {
    const lookupFields = allFieldsForPairing || fields;

    return (
      <div
        className={cn(
          "grid grid-cols-[minmax(0,25%),1fr] gap-y-2 gap-x-4",
          containerClassName,
          additionalClasses
        )}
      >
        {map(keys(fields), (fieldName) =>
          renderField(fieldName, fields[fieldName], lookupFields)
        )}
      </div>
    );
  };

  // The partner-contact field carries the record's selected partner in its
  // dependentValues; reuse it to pre-fill the quick-create modal's Partner.
  const entityCreatePartnerId = resolveCreatePartnerContactPartnerId(
    entityCreateField ? fieldsConfig[entityCreateField] : undefined
  );

  const entityCreateModals = (
    <>
      {entityCreateView === "partner" && (
        <CreatePartnerModal
          isOpen
          setIsOpen={(open) => {
            if (!open) closeEntityCreate();
          }}
          onSuccess={handleEntityCreated}
        />
      )}
      {entityCreateView === "partnerContact" && (
        <CreatePartnerContactModal
          isOpen
          setIsOpen={(open) => {
            if (!open) closeEntityCreate();
          }}
          onSuccess={handleEntityCreated}
          defaultPartnerId={entityCreatePartnerId}
        />
      )}
    </>
  );

  if (hideContainer) {
    return (
      <>
        {renderFieldsContainer(detailsFields)}
        {entityCreateModals}
      </>
    );
  }

  return (
    <div
      className={cn(
        "bg-surface-elevation2 border-[0.5px] border-border-border1 w-full rounded-xl relative",
        isQuickView
          ? "bg-surface-elevation2 p-4"
          : "bg-surface-elevation1 min-h-[30rem] p-6",
        className
      )}
    >
      <Text variant={isQuickView ? "lgLabel" : "lgSubtitleH1"} className="mb-4">
        {t("details")}
      </Text>

      {isQuickView ? (
        <>
          {isDrawerOpen && !hasSortedRef.current ? (
            <InlineLoader label={t("loading")} />
          ) : (
            <>
              {renderFieldsContainer(filledFields, undefined, {
                ...filledFields,
                ...emptyFields
              })}
              {!isEmpty(emptyFields) && (
                <div className="flex flex-col">
                  <div
                    className={cn(
                      "grid transition-[grid-template-rows] duration-200 ease-in-out",
                      isEmptyFieldsOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    )}
                  >
                    <div className="overflow-hidden">
                      {renderFieldsContainer(emptyFields, undefined, {
                        ...filledFields,
                        ...emptyFields
                      })}
                    </div>
                  </div>
                  <Button
                    variant="tertiary"
                    className="w-fit h-fit self-end"
                    onClick={() => setIsEmptyFieldsOpen(!isEmptyFieldsOpen)}
                  >
                    {t(isEmptyFieldsOpen ? "showLess" : "showMore")}
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {renderFieldsContainer(detailsFields)}
          {sections?.map((section) => (
            <div key={section.id} className="mt-6">
              <Text variant="lgSubtitleH2" className="mb-4">
                {section.name}
              </Text>
              <DetailFields
                entity={entity}
                fieldsConfig={section.fieldsConfig}
                onUpdateField={onUpdateField}
                containerClassName={containerClassName}
                tPrefix={tPrefix}
                context={context}
                hideContainer
              />
            </div>
          ))}
        </>
      )}
      {entityCreateModals}
    </div>
  );
};

export default DetailFields;
